// Added by Sejune Oh on 2020-03-17
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import React, { FC, useCallback } from 'react'
import { createStyles, makeStyles } from '@material-ui/core'

import useStore from '@/stores/global/hooks/useStore'

import { USER_ACCESS_LEVEL } from '@/helper/define'
import { Task, TASK_EXIT_CODE, TaskExitCode } from '@/helper/define/taskDefine'
import Intl from '@/helper/Intl'
import { getDateWithFormat, getTaskExecutionTime } from '@/helper/util'
import { Button } from '@/components/worklist/Button'
import LegacyAppIcon from '@/legacy-polymer/LegacyAppIcon'
import { COLOR_LIGTH_NAVY, FONT_COLOR_TITLE } from '@/styles/Theme'
import { TaskDetailStyleTypes } from '@/components/worklist/WebComponents/TaskDetailViewWrapper'

const COLOR_RUNNING = '#111A24'
const COLOR_CANCELED = '#221F16'
const COLOR_WAITING = '#1E201F'
const COLOR_DONE = '#121A0E'
const COLOR_FAILED = '#240707'
const COLOR_CRASHED = '#420e0e'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export interface TaskDetailViewProps {
  /** Task List에서 선택한  Task의 상세 정보 */
  task?: Task
  /** ShowLog Button click event handler */
  showLogBtnText?: string
  /** ShowLog Button Text */
  onShowLogBtnClick?: Function
  /** Cancel button click evt handler */
  onCancelTaskBtnClick: Function
  /** Restart button click evt handler */
  onRestartTaskBtnClick: Function
  checkEngineValid: (engineName: string) => boolean
  colNodeName?: string
  colProcName?: string
  colRuleName?: string
  colStatus?: string
  colProcExitCode?: string
  colCreatedAt?: string
  colProcStartedAt?: string
  colProcCompletedAt?: string
  colProcExecutionTime?: string
  cancelBtnText?: string
  restartBtnText?: string
  /** WC용 Styles  */
  classes?: TaskDetailStyleTypes
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/**
 * TaskList에서 선택한 Task의 상세 정보와 Log를 볼 수 있는 버튼을 제공하는 UI 입니다.
 */
const TaskDetailView: FC<TaskDetailViewProps> = ({
  task,
  showLogBtnText: showLogBtnTextParam,
  onShowLogBtnClick,
  colNodeName: colNodeNameParam,
  colProcName: colProcNameParam,
  colRuleName: colRuleNameParam,
  colStatus: colStatusParam,
  colProcExitCode: colProcExitCodeParam,
  colProcStartedAt: colProcStartedAtParam,
  colProcCompletedAt: colProcCompletedAtParam,
  colProcExecutionTime: colProcExecutionTimeParam,
  cancelBtnText: cancelBtnTextParam,
  restartBtnText: restartBtnTextParam,
  colCreatedAt: colCreatedAtParam,
  onCancelTaskBtnClick,
  onRestartTaskBtnClick,
  checkEngineValid,
  classes: webComponentClassses,
  ...props
}) => {
  const { userAuth } = useStore()

  const classes = webComponentClassses
    ? webComponentClassses['taskdetail']
    : makeStyles(() => createStyles({ ...customStyles }))()

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // ! I18n
  const abortBtnText = Intl.getIntlText({ id: 'htmljs_react.worklist.taskdetailview.btn_abort' })
  const colNodeName = colNodeNameParam || Intl.getIntlText({ id: 'htmljs_react.worklist.taskdetailview.col_node_name' })
  const colProcName = colProcNameParam || Intl.getIntlText({ id: 'htmljs_react.worklist.taskdetailview.col_proc_name' })
  const colStatus = colStatusParam || Intl.getIntlText({ id: 'htmljs_react.worklist.taskdetailview.col_status' })
  const colProcExitCode =
    colProcExitCodeParam || Intl.getIntlText({ id: 'htmljs_react.worklist.taskdetailview.col_proc_exit_code' })
  const colProcStartedAt =
    colProcStartedAtParam || Intl.getIntlText({ id: 'htmljs_react.worklist.taskdetailview.col_proc_started_at' })
  const colProcCompletedAt =
    colProcCompletedAtParam || Intl.getIntlText({ id: 'htmljs_react.worklist.taskdetailview.col_proc_completed_at' })
  const colProcExecutionTime =
    colProcExecutionTimeParam || Intl.getIntlText({ id: 'htmljs_react.worklist.taskdetailview.col_proc_execution_time' })
  const colCreatedAt = colCreatedAtParam || Intl.getIntlText({ id: 'htmljs_react.worklist.taskdetailview.col_created_at' })
  const colRuleName =  colRuleNameParam || Intl.getIntlText({ id: 'htmljs_react.worklist.taskdetailview.col_rule_name' })
  const showLogBtnText = showLogBtnTextParam || Intl.getIntlText({ id: 'htmljs_react.worklist.taskdetailview.btn_show_log' })
  const cancelBtnText = cancelBtnTextParam || Intl.getIntlText({ id: 'htmljs_react.worklist.taskdetailview.btn_cancel' })
  const restartBtnText = restartBtnTextParam || Intl.getIntlText({ id: 'htmljs_react.worklist.taskdetailview.btn_restart' })

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // ! Evt handler
  const handleShowLogBtnClick = () => {
    if (!onShowLogBtnClick) return
    onShowLogBtnClick()
  }
  const handleCancelTaskBtnClick = () => {
    if (!onCancelTaskBtnClick || checkCancelBtnDisabled()) return
    return onCancelTaskBtnClick(task!.id!, isAbortAble())
  }
  const handleRestartBtnClick = () => {
    if (!onRestartTaskBtnClick || checkRestartBtnDisabled()) return
    onRestartTaskBtnClick(task!.id!)
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // ! Functions
  const getTaskValue = (key: string) => {
    return task ? task[key] || '-' : '-'
  }
  const getTaskDateValue = (key: string) => {
    if (!task || !task[key]) return '-'

    return getDateWithFormat(task[key] as string)
  }
  const getTaskExitCode = () => {
    if (!task) return '-'
    if (!task.status || task.status === 'RUNNING' || task.status === 'WAITING' || task.status === 'RETRYING') return '-'

    if (!Object.hasOwnProperty.call(TASK_EXIT_CODE, task.proc_exit_code + '')) {
      return `${task.proc_exit_code} (Unexpected behaviour)`
    }

    return (
      task.proc_exit_code
        ? `${task.proc_exit_code} (${TASK_EXIT_CODE[(task.proc_exit_code + '') as TaskExitCode]})`
        : `-`
    )
  }
  const getExecutionTime = () => {
    if (!task) return '-'
    if (!task.status || task.status === 'RUNNING' || task.status === 'WAITING' || task.status === 'RETRYING') return '-'
    return getTaskExecutionTime(task.proc_started_at, task.proc_completed_at)
  }

  const getTaskProcessName = () => {
    if (!task) return '-'
    return task.proc_name + (task.proc_version ? ` (v${task.proc_version})` : '')
  }

  const getStatusBGIcon = () => {
    if (!task) return null

    switch (task.status) {
      case 'RUNNING':
        return (
          <LegacyAppIcon iconSize={300} iconName={'cl:aps-running'} title="Running" style={{ fill: COLOR_RUNNING }} />
        )
      case 'CANCELED':
        return (
          <LegacyAppIcon
            iconSize={300}
            iconName={'cl:aps-canceled'}
            title="Canceled"
            style={{ fill: COLOR_CANCELED }}
          />
        )
      case 'WAITING':
        return (
          <LegacyAppIcon iconSize={300} iconName={'cl:aps-waiting'} title="Waiting" style={{ fill: COLOR_WAITING }} />
        )
      case 'RETRYING':
        return (
          <LegacyAppIcon iconSize={300} iconName={'cl:aps-waiting'} title="Retrying" style={{ fill: COLOR_WAITING }} />
        )
      case 'DONE':
        return <LegacyAppIcon iconSize={300} iconName={'cl:aps-done'} title="Done" style={{ fill: COLOR_DONE }} />
      case 'FAILED':
        if (!Object.hasOwnProperty.call(TASK_EXIT_CODE, task.proc_exit_code + '')) {
          return (
            <LegacyAppIcon
              iconSize={300}
              iconName={'cl:aps-crashed'}
              title="Crashed"
              style={{ fill: COLOR_CRASHED}}
            />
          )
        }

        return (
          <LegacyAppIcon
            iconSize={300}
            iconName={'cl:aps-failed'}
            title="Failed"
            style={{ fill: COLOR_FAILED }} />
        )
      default:
        return null
    }
  }

  const checkRestartBtnDisabled = useCallback(() => {
    if (!task) return true

    return task.status === 'RUNNING' || task.status === 'WAITING' || task.status === 'RETRYING'  || task.status === 'INVOKING' || !checkEngineValid(task.proc_name!)
    // eslint-disable-next-line
  }, [task])

  const checkCancelBtnDisabled = useCallback(() => {
    if (!task) return true
    const userLevel = Number(userAuth.userInfo.access_level)
    const ADMIN_LEVEL = USER_ACCESS_LEVEL.AccessLevelAdmin.enumValue

    // user level이 admin level 이상인 경우라면 Running 중에도 cancel 시킬 수 있다
    if (userLevel >= ADMIN_LEVEL) {
      return task.status === 'CANCELED' || task.status === 'INVOKING' || task.status === 'DONE' || task.status === 'FAILED'
    }

    return task.status !== 'WAITING' && task.status !== 'RETRYING'
    // eslint-disable-next-line
  }, [task])

  const isAbortAble = useCallback(() => {
    if (!task) return false

    const userLevel = Number(userAuth.userInfo.access_level)
    const ADMIN_LEVEL = USER_ACCESS_LEVEL.AccessLevelAdmin.enumValue

    return ((userLevel >= ADMIN_LEVEL) && task.status === 'RUNNING') 
    // eslint-disable-next-line
  }, [task])

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // ! Render
  return (
    <div className={classes.section}>
      <div className={classes.sectionBG}>{getStatusBGIcon()}</div>
      <div className={classes.infoWrapper}>
        {/* -------- ROW START --------  */}
        <div className={classes.infoRow}>
          <div>{colStatus}</div>
          <div>{getTaskValue('status')}</div>
        </div>
        <div className={classes.infoRow}>
          <div>{colNodeName}</div>
          <div>{getTaskValue('assigned_node_key')}</div>
        </div>
        <div className={classes.infoRow}>
          <div>{colCreatedAt}</div>
          <div>{getTaskDateValue('created_at')}</div>
        </div>
        <div className={classes.infoRow}>
          <div>{colRuleName}</div>
          <div>{getTaskValue('rule_name')}</div>
        </div>
        <div className={classes.infoRow}>
          <div>{colProcName}</div>
          <div>{getTaskProcessName()}</div>
        </div>
        <div className={classes.infoRow}>
          <div>{colProcExitCode}</div>
          <div>{getTaskExitCode()}</div>
        </div>
        <div className={classes.infoRow}>
          <div>{colProcExecutionTime}</div>
          <div>{getExecutionTime()}</div>
        </div>
        <div className={classes.infoRow}>
          <div>{colProcStartedAt}</div>
          <div>{getTaskDateValue('proc_started_at')}</div>
        </div>
        <div className={classes.infoRow}>
          <div>{colProcCompletedAt}</div>
          <div>{getTaskDateValue('proc_completed_at')}</div>
        </div>

      </div>
      {/* -------- ROW END --------  */}
      <div className={classes.controlWrapper}>
        <div>
          <Button
            classes={webComponentClassses}
            disabled={checkCancelBtnDisabled()}
            onBtnClick={handleCancelTaskBtnClick}
          >
            {isAbortAble() ? abortBtnText : cancelBtnText}
          </Button>
          <Button
            classes={webComponentClassses}
            disabled={checkRestartBtnDisabled()}
            onBtnClick={handleRestartBtnClick}
          >
            {restartBtnText}
          </Button>
        </div>
        <div>
          <Button classes={webComponentClassses} disabled={!onShowLogBtnClick} onBtnClick={handleShowLogBtnClick}>
            {showLogBtnText}
          </Button>
        </div>
      </div>
    </div>
  )
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// ! Style
export const customStyles: Record<string, any> = {
  section: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    height: '100%',
    fontSize: '11px',
    position: 'relative',
    backgroundColor: '#000',
  },
  sectionBG: {
    zIndex: 0,
    position: 'absolute',
    bottom: 0,
    right: `-44px`,
  },
  infoWrapper: {
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    color: FONT_COLOR_TITLE,
    maxHeight: 'calc(100% - 44px)',
    overflow: 'auto',
    position: 'relative',
    '&::-webkit-scrollbar-thumb': {
      width: '50px',
      height: '50px',
      borderRadius: '4px',
      background: '#646464',
    },
    '&::-webkit-scrollbar': {
      width: '8px',
      height: '8px',
    },
    '&:before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 0,
      bottom: 0,
      width: '157px',
      backgroundColor: '#1d1e1f',
    },
  },
  infoRow: {
    display: 'flex',
    zIndex: 1,
    '& > div': {
      height: '36px',
      display: 'flex',
      alignItems: 'center',
      paddingLeft: '20px',
      '&:first-of-type': {
        width: '157px',
        backgroundColor: '#1d1e1f',
        boxSizing: 'border-box',
      },
    },
  },
  controlWrapper: {
    zIndex: 1,
    background: COLOR_LIGTH_NAVY,
    height: '40px',
    paddingTop: '4px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingRight: '8px',
    '& > div': {
      display: 'flex',
      '& > button': {
        marginRight: '4px',
        '&:last-of-type': {
          marginLeft: '4px',
        },
      },
    },
  },
}

export default TaskDetailView
